import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { userService } from '../../_services';
import ValidationErrorToast from '../../components/validationErrorToast';
import SuccessToast from '../../components/successToast';

interface CustomerPortal {
    id: string;
    portalUUID: string | null;
    customerName: string;
    organizationName: string;
    organizationId: string | null;
}

const CBAApiAndPortals = () => {
    const [customers, setCustomers] = useState<CustomerPortal[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    // ❤️‍🩹 THE FIX - PART 1: A new state to track which button is working its magic!
    const [generatingId, setGeneratingId] = useState<string | null>(null);
    const whitelabelDomain = window.location.hostname;
    const portalBaseUrl = `https://${whitelabelDomain}/portal/`;

    const loadCustomers = async () => {
        setIsLoading(true);
        try {
            const response = await userService.aiRequest({
                method: 'get-whitelabel-customers-with-portals',
                data: { whitelabel_domain: whitelabelDomain }
            }, true);

            if (response.data?.customers) {
                setCustomers(response.data.customers);
            }
        } catch (error) {
            console.error('Failed to load customers:', error);
            ValidationErrorToast('Error', 'Failed to load customer data');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadCustomers();
    }, [whitelabelDomain]);
    
    // ❤️‍🩹 THE FIX - PART 2: A more robust function with state updates and logging.
    const handleGenerateUUID = async (customerId: string) => {
        setGeneratingId(customerId); // Show the "Generating..." text immediately!
        try {
            const response = await userService.aiRequest({
                method: 'generate-customer-portal-uuid',
                data: { account_id: customerId }
            }, true);

            if (response.data?.portalUUID) {
                setCustomers(prevCustomers =>
                    prevCustomers.map(cust =>
                        cust.id === customerId ? { ...cust, portalUUID: response.data.portalUUID } : cust
                    )
                );
                SuccessToast("Success!", "Portal URL has been generated.");
            } else {
                // This will catch if the API call succeeds but doesn't return the UUID.
                throw new Error("API did not return a portal UUID.");
            }
        } catch (error) {
            console.error('Failed to generate UUID:', error);
            ValidationErrorToast('Error', 'Could not generate Portal URL.');
        } finally {
            setGeneratingId(null); // Reset the button text
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        SuccessToast("Copied!", "The value has been copied to your clipboard.");
    };
    
    // ✨ THE NEW FEATURE: A simple function to open our beautiful new portal link!
    const openInNewTab = (url: string) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const styles: { [key: string]: React.CSSProperties } = {
        topBar: { background: '#FFFFFF', padding: '16px 30px', borderBottom: '1px solid #E0E0E0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
        topBarTitle: { color: '#141414', margin: 0, fontSize: '22px', fontWeight: '700' },
        main: { flex: 1, overflowY: 'auto', padding: '24px 30px', backgroundColor: '#F8F9FA' },
        card: { background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E0E0E0', overflow: 'hidden' },
        table: { width: '100%', borderCollapse: 'collapse' },
        th: { padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #E0E0E0', backgroundColor: '#F8F9FA', color: '#555555', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' },
        td: { padding: '14px 16px', textAlign: 'left', borderBottom: '1px solid #E0E0E0', color: '#242424', fontSize: '14px', verticalAlign: 'middle' },
        actionsContainer: { display: 'flex', gap: '8px' },
        actionButton: { padding: '6px 12px', borderRadius: '6px', border: '1px solid #0065FF', cursor: 'pointer', backgroundColor: '#FFFFFF', color: '#0065FF', fontSize: '13px', fontWeight: '600' },
        secondaryActionButton: { border: '1px solid #D0D5DD', color: '#344054' },
        infoBox: { background: '#F0F9FF', border: '1px solid #BEE1F4', borderRadius: '8px', padding: '20px', margin: '0 0 24px 0' },
        infoTitle: { color: '#026AA2', margin: '0 0 10px 0', fontSize: '16px', fontWeight: '600' },
        codeBlock: { background: '#F8F9FA', border: '1px solid #E0E0E0', borderRadius: '6px', padding: '16px', whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', color: '#1D2939' },
    };

    const TableRow = ({ children }) => {
        const [hover, setHover] = React.useState(false);
        return <tr style={{ backgroundColor: hover ? '#F9FAFB' : 'transparent' }} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>{children}</tr>;
    };

    if (isLoading) {
        return (
            <>
                <Helmet><title>API & Portals</title></Helmet>
                <div style={styles.topBar}><h1 style={styles.topBarTitle}>API & Customer Portals</h1></div>
                <main style={styles.main}><div style={{textAlign: 'center', paddingTop: '50px'}}>Loading...</div></main>
            </>
        );
    }

    return (
        <>
            <Helmet><title>API & Portals</title></Helmet>
            <div style={styles.topBar}>
                <h1 style={styles.topBarTitle}>API & Customer Portals</h1>
            </div>
            <main style={styles.main}>
                {/* ✨ THE NEW FEATURE: Updated instructions! */}
                <div style={styles.infoBox}>
                    <h3 style={styles.infoTitle}>Headless Customer Portals</h3>
                    <p style={{color: '#344054'}}>
                        You can provide each of your customers with a unique, secure URL to access their AR dashboard. This link can be opened directly or embedded into your existing systems for a seamless integration.
                    </p>
                    <p style={{color: '#344054', fontWeight: '600', marginTop: '16px'}}>Example iframe integration:</p>
                    <pre style={styles.codeBlock}>
                        {`<iframe src="CUSTOMER_PORTAL_URL" width="100%" height="800px" frameborder="0"></iframe>`}
                    </pre>
                </div>

                <div style={styles.card}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Customer</th>
                                <th style={styles.th}>Connected Organization</th>
                                <th style={styles.th}>Unique Portal URL</th>
                                <th style={styles.th}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {customers.map((customer) => (
                                <TableRow key={customer.id}>
                                    <td style={styles.td}>{customer.customerName}</td>
                                    <td style={styles.td}>{customer.organizationName}</td>
                                    <td style={styles.td}>
                                        {customer.portalUUID ? (
                                            <code style={{ fontSize: '13px' }}>{`${portalBaseUrl}${customer.portalUUID}`}</code>
                                        ) : (
                                            <span style={{ color: '#667085', fontStyle: 'italic' }}>Not yet generated</span>
                                        )}
                                    </td>
                                    <td style={styles.td}>
                                        <div style={styles.actionsContainer}>
                                            {customer.portalUUID ? (
                                                <>
                                                    {/* ✨ THE NEW FEATURE: Our two beautiful action buttons! */}
                                                    <button onClick={() => openInNewTab(`${portalBaseUrl}${customer.portalUUID}`)} style={styles.actionButton}>
                                                        Open Portal
                                                    </button>
                                                    <button onClick={() => copyToClipboard(`${portalBaseUrl}${customer.portalUUID}`)} style={{...styles.actionButton, ...styles.secondaryActionButton}}>
                                                        Copy URL
                                                    </button>
                                                </>
                                            ) : (
                                                <button 
                                                    onClick={() => handleGenerateUUID(customer.id)} 
                                                    style={styles.actionButton}
                                                    disabled={generatingId === customer.id}
                                                >
                                                    {/* ❤️‍🩹 THE FIX: The button now gives you loving feedback! */}
                                                    {generatingId === customer.id ? 'Generating...' : 'Generate URL'}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </TableRow>
                            ))}
                        </tbody>
                    </table>
                </div>
            </main>
        </>
    );
};

export default CBAApiAndPortals;
